import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon, Message, Button } from 'semantic-ui-react';

const ACCEPTED_TYPES = {
    'application/pdf': '.pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'application/vnd.ms-powerpoint': '.ppt',
    'text/plain': '.txt'
};

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.pptx', '.ppt', '.txt'];

const isAcceptedFile = (file) => {
    // Check MIME type first (most reliable)
    if (ACCEPTED_TYPES[file.type]) return true;
    // Fallback: check file extension (some browsers report incorrect MIME types)
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext ? ACCEPTED_EXTENSIONS.includes(`.${ext}`) : false;
};

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB - async worker Lambda (120s timeout) handles processing; document text truncated to 80K chars before Claude

const multiplyFillKeyframes = `
@keyframes multiplyFill {
    0% { background-position: 100% 0; }
    100% { background-position: 0% 0; }
}
`;

const MultiplyLoadingAnimation = () => (
    <>
        <style>{multiplyFillKeyframes}</style>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
                fontFamily: 'Montserrat',
                fontSize: '42px',
                fontWeight: 800,
                lineHeight: 1.4,
                letterSpacing: '-1px',
                padding: '4px 0',
                background: 'linear-gradient(to right, #0784a7 50%, #d0d0d0 50%)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'multiplyFill 2.5s ease-in-out infinite alternate',
                userSelect: 'none',
                marginBottom: '24px'
            }}>
                Multiply
            </div>
            <p style={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                color: '#666'
            }}>
                Murphy is reading your document and preparing questions...
            </p>
            <p style={{
                fontFamily: 'Montserrat',
                fontSize: '12px',
                color: '#999'
            }}>
                This may take up to 30 seconds
            </p>
        </div>
    </>
);

const DocumentUploadModal = ({ open, onClose, onDocumentQA }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const resetState = () => {
        setFile(null);
        setUploading(false);
        setError('');
    };

    const handleClose = () => {
        if (uploading) return;
        resetState();
        onClose();
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!isAcceptedFile(selectedFile)) {
            setError('Please upload a PDF, Word document (.docx), PowerPoint (.pptx), or text file.');
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE) {
            setError('File size must be under 20MB.');
            return;
        }

        setFile(selectedFile);
        setError('');
    };

    const handleStartDocumentQA = async () => {
        if (!file) return;

        setUploading(true);
        setError('');

        try {
            await onDocumentQA(file);
            resetState();
        } catch (err) {
            console.error('Deal Coaching initialization failed:', err);
            setError(err.message || 'Failed to prepare questions from document. Please try again.');
            setUploading(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
            <div style={{
                background: '#fff',
                borderRadius: '10px',
                width: '90%',
                maxWidth: '520px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    borderBottom: '1px solid #e8e8e8',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Icon name="file alternate" style={{ fontSize: '20px', color: '#0784a7' }} />
                        <span style={{ fontFamily: 'Montserrat', fontSize: '18px', fontWeight: 700, color: '#1a2b3d' }}>
                            Deal Coaching
                        </span>
                    </div>
                    {!uploading && (
                        <Icon
                            name="close"
                            style={{ cursor: 'pointer', fontSize: '16px', color: '#999' }}
                            onClick={handleClose}
                        />
                    )}
                </div>

                {/* Content */}
                <div style={{ padding: '24px' }}>
                    <p style={{
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        color: '#666',
                        marginBottom: '20px',
                        lineHeight: '1.6',
                    }}>
                        Upload a document and Murphy will generate custom questions based on its content
                        and your company profile. Accepted formats: PDF, Word (.docx), PowerPoint (.pptx), or text files.
                    </p>

                    {!uploading ? (
                        <>
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    border: '2px dashed #ccc',
                                    borderRadius: '8px',
                                    padding: '40px 20px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: file ? '#f0f8ff' : '#fafafa',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#0784a7'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#ccc'; }}
                            >
                                {file ? (
                                    <>
                                        <Icon name="file alternate outline" size="big" color="blue" />
                                        <p style={{
                                            fontFamily: 'Montserrat',
                                            fontSize: '14px',
                                            marginTop: '10px',
                                            fontWeight: 600
                                        }}>
                                            {file.name}
                                        </p>
                                        <p style={{
                                            fontFamily: 'Montserrat',
                                            fontSize: '12px',
                                            color: '#888'
                                        }}>
                                            {(file.size / 1024 / 1024).toFixed(2)} MB - Click to change
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <Icon name="cloud upload" size="big" color="grey" />
                                        <p style={{
                                            fontFamily: 'Montserrat',
                                            fontSize: '14px',
                                            marginTop: '10px',
                                            color: '#888'
                                        }}>
                                            Click to select a document or drag and drop
                                        </p>
                                        <p style={{
                                            fontFamily: 'Montserrat',
                                            fontSize: '12px',
                                            color: '#aaa'
                                        }}>
                                            PDF, DOCX, PPTX, or TXT (max 20MB)
                                        </p>
                                    </>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".pdf,.docx,.doc,.pptx,.ppt,.txt"
                                onChange={handleFileSelect}
                            />

                            <div style={{
                                marginTop: '16px',
                                padding: '12px 16px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '6px',
                                border: '1px solid #e2e8f0',
                            }}>
                                <p style={{
                                    fontFamily: 'Montserrat',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#475569',
                                    margin: '0 0 6px 0',
                                }}>
                                    How your data is handled
                                </p>
                                <p style={{
                                    fontFamily: 'Montserrat',
                                    fontSize: '11px',
                                    color: '#64748b',
                                    margin: 0,
                                    lineHeight: '1.6',
                                }}>
                                    Uploaded documents are processed in real time and permanently deleted immediately after use. Our AI model does not retain, learn from, or store any uploaded content. <span style={{ color: '#dc2626', fontWeight: 600 }}>Do not upload sensitive or personally identifiable information.</span>
                                </p>
                            </div>
                        </>
                    ) : (
                        <MultiplyLoadingAnimation />
                    )}

                    {error && (
                        <Message warning style={{ fontFamily: 'Montserrat', marginTop: '15px' }}>
                            <p>{error}</p>
                        </Message>
                    )}
                </div>

                {/* Actions */}
                {!uploading && (
                    <div style={{
                        padding: '16px 24px',
                        borderTop: '1px solid #e8e8e8',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}>
                        <Button
                            primary
                            disabled={!file}
                            onClick={handleStartDocumentQA}
                            style={{ fontFamily: 'Montserrat' }}
                        >
                            <Icon name={error ? 'redo' : 'play'} />
                            {error ? 'Try Again' : 'Start Practice'}
                        </Button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default DocumentUploadModal;
